import React from 'react';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { formatMoney } from '../../theme/chart';
import { personColor, tokens } from '../../theme/tokens';
import { Meter, Panel, PanelHeader, Quiet } from './home/ui';

export interface MemberSpending {
  userId: string;
  firstName: string;
  /** what this member paid in the period */
  paid: number;
  /** this member's part of what the group spent in the period */
  part: number;
  /** position in the group, for the member's color */
  index: number;
}

interface Props {
  members: MemberSpending[];
  currency: string;
  currentUserId?: string;
}

/**
 * "Quem pagou": what each member paid in the month, in their own color,
 * with a mark at their part of it — so "paid more than their part" reads at
 * a glance, before the balances say what it means in money.
 */
export const MemberSpendingCard: React.FC<Props> = ({ members, currency, currentUserId }) => {
  const { t, i18n } = useTranslation('dashboard');
  const money = (value: number) => formatMoney(value, currency, i18n.language);
  const title = t('memberSpending.title');
  const shown = members.filter((member) => member.paid > 0 || member.part > 0);
  const max = Math.max(...shown.map((member) => Math.max(member.paid, member.part)), 1);
  const parts = Array.from(new Set(shown.map((member) => Math.round(member.part))));
  // An even split says it once in the header; uneven parts are on each bar.
  const aside = parts.length === 1 && shown.length > 1 ? t('memberSpending.eachPart', { amount: money(parts[0]) }) : undefined;

  return (
    <Panel label={title} sx={{ gap: 2 }}>
      <PanelHeader title={title} aside={aside} />
      {shown.length === 0 ? (
        <Quiet>{t('memberSpending.empty')}</Quiet>
      ) : (
        <>
          <Box component='ul' sx={{ m: 0, p: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 1.75 }}>
            {shown.map((member) => {
              const diff = Math.round(member.paid - member.part);
              const person = personColor(member.index);
              return (
                <Box component='li' key={member.userId} sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, fontSize: 14, flexWrap: 'wrap' }}>
                    <Box component='span' sx={{ fontWeight: 600 }}>
                      {member.userId === currentUserId ? t('memberSpending.you') : member.firstName}
                    </Box>
                    <Box component='span' sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      <strong>{money(member.paid)}</strong>
                      {diff !== 0 && (
                        <Box component='span' sx={{ color: diff > 0 ? tokens.accent : tokens.ink3, fontWeight: diff > 0 ? 600 : 400 }}>
                          {' · '}
                          {t(diff > 0 ? 'memberSpending.more' : 'memberSpending.less', { amount: money(Math.abs(diff)) })}
                        </Box>
                      )}
                    </Box>
                  </Box>
                  <Meter ratio={member.paid / max} color={person.color} height={10} marker={member.part / max} />
                </Box>
              );
            })}
          </Box>
          <Box component='span' sx={{ fontSize: 12.5, color: tokens.ink3 }}>{t('memberSpending.markHint')}</Box>
        </>
      )}
    </Panel>
  );
};
